import {
  FEEDBACKS_REQUEST,
  FEEDBACKS_FAILURE,
  FETCH_FEEDBACKS_SUCCESS,
  CREATE_FEEDBACKS_SUCCESS,
  DELETE_FEEDBACKS_SUCCESS,
  UPDATE_FEEDBACKS_SUCCESS,
  GET_FEEDBACKS_SUCCESS,
  FETCH_ESCALATIONS_SUCCESS,
  REALTIME_FEEDBACK_RECEIVED,
} from "../action/types";

const initialState = {
  feedbacks: [],
  total: 0,
  escalations: [],
  currentFeedback: null,
  loading: false,
  error: null,
};

const normalizeId = (value) => {
  if (value === undefined || value === null) return "";
  if (typeof value === "object") {
    return String(value.id || value._id || value.toString?.() || "");
  }
  return String(value);
};

const getFeedbackId = (feedback = {}) => {
  if (!feedback) return "";
  return normalizeId(feedback.id || feedback._id);
};

const getRealtimeFeedback = (payload = {}) => payload.feedback || payload;

const updateFeedbackInList = (feedbacks, feedback) => {
  const feedbackId = getFeedbackId(feedback);
  if (!feedbackId) return { items: feedbacks, found: false };

  let found = false;
  const items = feedbacks.map((item) => {
    if (getFeedbackId(item) !== feedbackId) return item;
    found = true;
    return feedback;
  });

  return { items, found };
};

const isEscalatedFeedback = (feedback = {}) =>
  !!(
    feedback?.isEscalated ||
    Number(feedback?.escalationLevel || 0) > 0 ||
    feedback?.escalatedAt ||
    (Array.isArray(feedback?.escalationHistory) &&
      feedback.escalationHistory.length > 0) ||
    (Array.isArray(feedback?.escalationLog) &&
      feedback.escalationLog.length > 0)
  );

const feedbacksReducer = (state = initialState, action) => {
  const { type, payload } = action;

  switch (type) {
    case FEEDBACKS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case FETCH_FEEDBACKS_SUCCESS:
      return {
        ...state,
        loading: false,
        feedbacks: Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
            ? payload
            : [],
        total: payload?.total ?? (Array.isArray(payload) ? payload.length : 0),
      };

    case FETCH_ESCALATIONS_SUCCESS:
      return {
        ...state,
        loading: false,
        escalations: Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
            ? payload
            : [],
      };

    case GET_FEEDBACKS_SUCCESS:
      return {
        ...state,
        loading: false,
        currentFeedback: payload,
        feedbacks: state.feedbacks.map((item) =>
          getFeedbackId(item) === getFeedbackId(payload) ? payload : item,
        ),
        escalations: state.escalations.map((item) =>
          getFeedbackId(item) === getFeedbackId(payload) ? payload : item,
        ),
      };

    case CREATE_FEEDBACKS_SUCCESS: {
      const feedbackId = getFeedbackId(payload);
      const existing = state.feedbacks.some(
        (item) => getFeedbackId(item) === feedbackId,
      );
      const feedbacks = feedbackId
        ? [
            payload,
            ...state.feedbacks.filter(
              (item) => getFeedbackId(item) !== feedbackId,
            ),
          ]
        : [payload, ...state.feedbacks];

      return {
        ...state,
        loading: false,
        feedbacks,
        total: existing ? state.total : state.total + 1,
      };
    }

    case UPDATE_FEEDBACKS_SUCCESS: {
      const { items } = updateFeedbackInList(state.feedbacks, payload);
      const { items: escalations } = updateFeedbackInList(
        state.escalations,
        payload,
      );
      return {
        ...state,
        loading: false,
        currentFeedback:
          getFeedbackId(state.currentFeedback) === getFeedbackId(payload)
            ? payload
            : state.currentFeedback,
        feedbacks: items,
        escalations,
      };
    }

    case REALTIME_FEEDBACK_RECEIVED: {
      const feedback = getRealtimeFeedback(payload);
      const feedbackId = getFeedbackId(feedback);
      if (!feedbackId) return state;

      const realtimeAction = payload?.action || "updated";
      const { items, found } = updateFeedbackInList(state.feedbacks, feedback);
      const { items: escalationItems, found: foundEscalation } =
        updateFeedbackInList(state.escalations, feedback);
      const currentFeedback =
        getFeedbackId(state.currentFeedback) === feedbackId
          ? feedback
          : state.currentFeedback;
      const escalations =
        isEscalatedFeedback(feedback) && !foundEscalation
          ? [feedback, ...state.escalations]
          : escalationItems;

      if (realtimeAction === "created") {
        return {
          ...state,
          currentFeedback,
          feedbacks: [
            feedback,
            ...state.feedbacks.filter(
              (item) => getFeedbackId(item) !== feedbackId,
            ),
          ],
          total: found ? state.total : state.total + 1,
          escalations,
        };
      }

      if (!found) {
        return {
          ...state,
          currentFeedback,
          escalations,
        };
      }

      return {
        ...state,
        currentFeedback,
        feedbacks: items,
        escalations,
      };
    }

    case DELETE_FEEDBACKS_SUCCESS:
      return {
        ...state,
        loading: false,
        feedbacks: state.feedbacks.filter(
          (item) => getFeedbackId(item) !== normalizeId(payload),
        ),
        total: Math.max(0, state.total - 1),
      };

    case FEEDBACKS_FAILURE:
      return {
        ...state,
        loading: false,
        error: payload,
      };

    default:
      return state;
  }
};

export default feedbacksReducer;
